/**
 * This component controls playback for scratchpad talkthroughs. It delegates
 * presentation to scratchpad-playback-bar and hooks onto the liveEditor object
 * to get information about the current playback state. It looks like the kind
 * of place we'd be able to use BackboneMixin, but the ScratchpadRecord object
 * in question from the live-editor package doesn't use get/set to store info.
 */
import * as React from "react";

import PlaybackBar from "./scratchpad-playback-bar.tsx";
import type {LiveEditor} from "./types.ts";

// TODO(LP-7162): create actual types for these
type ScratchpadRecord = any;

type Props = {
    // A promise that will resolve when we have the ScratchpadRecord
    liveEditorPromise: Promise<LiveEditor>;
    // Fires during a seek
    onSeek: (timeInSeconds: number) => unknown;
    // When the seeking ends
    onSeekEnd: () => unknown;
    // When the seeking begins
    onSeekStart: () => unknown;
    // Optional link to YouTube version
    youtubeUrl: string;
};

type State = {
    // Current point in playback
    currentTime: number;
    // The amount of time we think we can play
    durationEstimate: number;
    // A bunch of handlers we may as well store here
    handlers: Record<string, () => unknown> | null | undefined;
    // The LiveEditor reference
    liveEditor: LiveEditor;
    // Whether we are currently playing
    playing: boolean;
    // Whether the LiveEditor has deemed the audio ready to play
    readyToPlay: boolean;
    // The ScratchpadRecord reference
    record: ScratchpadRecord | null | undefined;
    // The time we are seeking to, which we'll hold onto while the
    // model actually seeks so the bar doesn't jump around
    seekTime: number | null | undefined;
    // The total playback time
    totalTime: number | null | undefined;
    // Whether we were playing before starting to seek
    wasPlaying: boolean | null | undefined;
};

class ScratchpadTalkthroughPlaybackBar extends React.Component<Props, State> {
    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted: boolean;

    static defaultProps: {
        onSeek: () => void;
        onSeekEnd: () => void;
        onSeekStart: () => void;
    } = {
        onSeek: () => {},
        onSeekEnd: () => {},
        onSeekStart: () => {},
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            currentTime: 0,
            durationEstimate: 0,
            handlers: null,
            liveEditor: null,
            playing: false,
            readyToPlay: false,
            record: null,
            seekTime: null,
            totalTime: null,
            wasPlaying: null,
        };
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        this.props.liveEditorPromise.then(this.handleLiveEditorReady);
    }

    componentWillUnmount() {
        const {liveEditor, record, handlers, playing} = this.state;

        if (playing && record) {
            record.pausePlayback();
        }

        if (!handlers) {
            // We don't need to remove any listeners
            return;
        }

        for (const key in handlers) {
            if (record && handlers.hasOwnProperty(key)) {
                record.off(key, handlers[key]);
            }
        }

        liveEditor.off("readyToPlay", this.handleReadyToPlay);
        this._isMounted = false;
    }

    handleLiveEditorReady: (liveEditor: LiveEditor) => void = (
        liveEditor: LiveEditor,
    ) => {
        const record = liveEditor.record;
        const handlers = {
            // Loading handler
            loading: this.handleLoading,
            // Play has started
            playStarted: this.handlePlay,
            // Play has ended
            playPaused: this.handlePause,
            playEnded: this.handlePause,
            playStopped: this.handlePause,
            // Tracking update
            playUpdate: this.handlePlayUpdate,
            seekDone: this.handlePlayUpdate,
        } as const;
        for (const key in handlers) {
            if (handlers.hasOwnProperty(key)) {
                record.on(key, handlers[key]);
            }
        }
        liveEditor.on("readyToPlay", this.handleReadyToPlay);

        if (!this._isMounted) {
            return;
        }
        this.setState({
            handlers: handlers,
            liveEditor: liveEditor,
            record: record,
            readyToPlay: !!liveEditor.audioReadyToPlay(),
        });
    };

    handlePlay: () => void = () => {
        this.setState({playing: true});
    };

    handlePlayUpdate: () => void = () => {
        const {record} = this.state;
        if (record) {
            this.setState({
                currentTime: record.currentTime(),
                seekTime: null,
            });
        }
    };

    handleLoading: () => void = () => {
        const {record} = this.state;
        if (record) {
            this.setState({durationEstimate: record.durationEstimate});
        }
    };

    handlePause: () => void = () => {
        this.setState({playing: false});
    };

    handleReadyToPlay: () => void = () => {
        this.setState({readyToPlay: true});
    };

    handleSeekStart: () => void = () => {
        const {playing} = this.state;
        this.props.onSeekStart();
        this.setState({wasPlaying: playing});
    };

    handleSeek: (time: number) => void = (time: number) => {
        this.props.onSeek(time);
        this.setState({seekTime: time});
    };

    handleSeekEnd: () => void = () => {
        this.props.onSeekEnd();
        this.setState({wasPlaying: null});
    };

    render(): React.ReactNode {
        const {
            currentTime,
            durationEstimate,
            playing,
            readyToPlay,
            seekTime,
            record,
            wasPlaying,
        } = this.state;
        return record ? (
            <PlaybackBar
                currentTime={seekTime !== null ? seekTime : currentTime}
                readyToPlay={readyToPlay}
                playing={playing || !!wasPlaying}
                // This is dom.PLAYBAR_PLAY from live-editor.js
                // If we remove it we will no longer be able to hide the large play
                // button that appears before the talkthrough is started.
                playButtonClassName="scratchpad-playbar-play"
                onClickPlay={() =>
                    playing ? record.pausePlayback() : record.play()
                }
                onSeek={this.handleSeek}
                onSeekStart={this.handleSeekStart}
                onSeekEnd={this.handleSeekEnd}
                totalTime={durationEstimate}
                youtubeUrl={this.props.youtubeUrl}
            />
        ) : null;
    }
}

export default ScratchpadTalkthroughPlaybackBar;
